"use client";

import { ChevronDownIcon, MonitorIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { Panel, PanelHeader, PanelTitle } from "../panel";

export function Gears() {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Panel id="gears">
      <PanelHeader>
        <PanelTitle>
          My Gears
        </PanelTitle>
      </PanelHeader>

      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <div className="flex justify-between items-center px-4 py-4 cursor-pointer">
            <div className="flex items-center gap-2">
              <MonitorIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Click to see my gears and tools</span>
            </div>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </Button>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="px-4 py-5">
            <p className="mb-6 text-sm text-zinc-600 dark:text-zinc-400">
              My gears and tools I use to get my work done.
            </p>

            {/* Devices Section */}
            <div className="mb-8">
              <h3 className="mb-4 text-lg font-medium">Devices</h3>
              <div className="space-y-3">
                <DeviceItem icon="💻" name="Vivobook Pro 15" />
                <DeviceItem icon="⌨️" name="Cosmic Byte Firefly Mechanical Keyboard" />
                <DeviceItem icon="🖱️" name="Portronic Hydra10 Mouse" />
                <DeviceItem icon="🎧" name="Crossbeats Roar 2.0 (Special Addition)" />
                <DeviceItem icon="📱" name="Samsung S23 (256 GB)" />
              </div>
            </div>

            {/* Web Extensions Section */}
            <div className="mb-8">
              <h3 className="mb-4 text-lg font-medium">Web Extensions</h3>
              <div className="space-y-3">
                <ExtensionItem number="1" name="Unhook" url="https://unhook.app/" />
                <ExtensionItem number="2" name="uBlock Origin" url="https://ublockorigin.com/" />
                <ExtensionItem number="3" name="React Developer Tools" url="https://react.dev/learn/react-developer-tools" />
                <ExtensionItem number="4" name="daily.dev" url="https://daily.dev/" />
                <ExtensionItem number="5" name="Grammarly" url="https://www.grammarly.com/" />
                <ExtensionItem number="6" name="Wappalyzer" url="https://www.wappalyzer.com/" />
                <ExtensionItem number="7" name="ColorZilla" url="https://www.colorzilla.com/" />
              </div>
            </div>

            {/* Software Section */}
            <div>
              <h3 className="mb-4 text-lg font-medium">Software</h3>
              <div className="space-y-3">
                <SoftwareItem number="1" name="Dia" url="https://dia-installer.de/" />
                <SoftwareItem number="2" name="Zen Browser" url="https://www.zenbrowser.com/" />
                <SoftwareItem number="3" name="Discord" url="https://discord.com/" />
                <SoftwareItem number="4" name="Notion" url="https://www.notion.so/" />
                <SoftwareItem number="5" name="TickTick" url="https://ticktick.com/" />
                <SoftwareItem number="6" name="Microsoft Power Toys" url="https://learn.microsoft.com/en-us/windows/powertoys/" />
                <SoftwareItem number="7" name="GitHub Desktop" url="https://desktop.github.com/" />
                <SoftwareItem number="8" name="Free Download Manager" url="https://www.freedownloadmanager.org/" />
                <SoftwareItem number="9" name="OBS Studio" url="https://obsproject.com/" />
                <SoftwareItem number="10" name="NVIDIA Broadcast" url="https://www.nvidia.com/en-us/geforce/broadcasting/broadcast-app/" />
                <SoftwareItem number="11" name="VLC" url="https://www.videolan.org/" />
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Panel>
  );
}

function DeviceItem({ icon, name }: { icon: string; name: string }) {
  return (
    <div className="flex items-center gap-3 rounded-md bg-zinc-50 px-3 py-2 dark:bg-zinc-800/50">
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-100 text-lg dark:bg-zinc-800">
        {icon}
      </div>
      <span className="text-sm">{name}</span>
    </div>
  );
}

function ExtensionItem({ number, name, url }: { number: string; name: string; url: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-sm dark:bg-zinc-800">
        {number}
      </div>
      <a 
        href={url}
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-zinc-800 hover:text-blue-600 dark:text-zinc-200 dark:hover:text-blue-400"
      >
        {name}
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1 h-3.5 w-3.5">
          <path d="M7 7h10v10"></path>
          <path d="M7 17 17 7"></path>
        </svg>
      </a>
    </div>
  );
}

function SoftwareItem({ number, name, url }: { number: string; name: string; url: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-sm dark:bg-zinc-800">
        {number}
      </div>
      <a 
        href={url}
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-zinc-800 hover:text-blue-600 dark:text-zinc-200 dark:hover:text-blue-400"
      >
        {name}
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1 h-3.5 w-3.5">
          <path d="M7 7h10v10"></path>
          <path d="M7 17 17 7"></path>
        </svg>
      </a>
    </div>
  );
}
